// ATO tax paid — the arithmetic behind the "Tax paid" tab on /ato-plans. Pure, CommonJS,
// Node-testable, shared with the daily alert function — so, as atoPlan.js: NO object/array
// spread and NO for…of (the web build would inject ESM helpers and webpack would then
// ignore module.exports).
//
// A tax ITEM is one thing the ATO asked for: an activity statement (BAS / IAS), a reminder
// notice, a notice of assessment… — who it's for, which period, how much and by when.
// Several documents can sit on one item (the statement, then the reminder about it).
// A PAYMENT is a bank receipt against an item. Everything sums in whole cents.
//
// "Tax paid in a year" means money that LEFT in that Australian financial year (by payment
// date), not the period the statement covers — the June-quarter BAS is paid in July, and
// that's the next year's cash.

const { cents, dollars, payByDate, daysBetween, todayIso, reminderStage } = require('./atoPlan');

const TAX_KINDS = ['BAS', 'IAS', 'PAYG instalment', 'Income tax', 'FBT', 'Super guarantee charge', 'Other'];
const DOC_TYPES = ['Activity statement', 'Reminder notice', 'Notice of assessment', 'Statement of account', 'Other'];

const isIso = (s) => /^\d{4}-\d{2}-\d{2}$/.test((s || '').toString());
const digits = (s) => (s || '').toString().replace(/\D/g, '');

// Australian financial year a date falls in, named by the year it ENDS: 2026-10-14 → 2027.
function fyOf(iso) {
  if (!isIso(iso)) return 0;
  const y = Number(iso.slice(0, 4));
  return Number(iso.slice(5, 7)) >= 7 ? y + 1 : y;
}
const fyLabel = (fy) => (fy ? `FY${String(fy - 1)}–${String(fy).slice(2)}` : '—');

const livePays = (payments, itemId) =>
  (payments || []).filter((p) => !p.deletedAt && (!itemId || p.itemId === itemId));

// Where one item stands.
//   refund  — the statement shows the ATO owes US (amount < 0)
//   nil     — nothing payable
//   paid    — covered (late = covered only after the due date)
//   part    — some paid, some owing
//   overdue — due date passed, still owing
//   due     — owing, not yet due
function workTaxItem(item, payments, today) {
  const t = today || todayIso();
  const amountC = cents(item.amount);
  const pays = livePays(payments, item.id).filter((p) => isIso(p.date))
    .sort((a, b) => a.date.localeCompare(b.date));
  let paidC = 0;
  let coveredOn = '';
  pays.forEach((p) => {
    paidC += cents(p.amount);
    if (!coveredOn && amountC > 0 && paidC >= amountC) coveredOn = p.date;
  });
  const owingC = Math.max(0, amountC - paidC);
  const payBy = isIso(item.dueDate) ? payByDate(item.dueDate) : '';
  let status;
  if (amountC < 0) status = 'refund';
  else if (amountC === 0 && paidC === 0) status = 'nil';
  else if (owingC === 0) status = (isIso(item.dueDate) && coveredOn > item.dueDate) ? 'late' : 'paid';
  else if (isIso(item.dueDate) && t > item.dueDate) status = 'overdue';
  else if (paidC > 0) status = 'part';
  else status = 'due';
  return {
    amount: dollars(amountC),
    paid: dollars(paidC),
    owing: dollars(owingC),
    overpaid: dollars(Math.max(0, paidC - Math.max(0, amountC))),
    payBy,
    coveredOn,
    status,
    daysToDue: isIso(item.dueDate) ? daysBetween(t, item.dueDate) : null,
    payments: pays,
  };
}

// Tax paid per entity in one financial year (by payment date), split by kind, plus what
// each entity still owes today (any year). fy = 0 → every year.
function taxSummary(items, payments, fy, today) {
  const live = (items || []).filter((i) => !i.deletedAt);
  const byId = {};
  live.forEach((i) => { byId[i.id] = i; });
  const rows = {};
  const row = (entity) => {
    if (!rows[entity]) rows[entity] = { entity, paidC: 0, owingC: 0, overdueC: 0, byKind: {}, count: 0 };
    return rows[entity];
  };
  livePays(payments).forEach((p) => {
    const it = byId[p.itemId];
    if (!it || !isIso(p.date)) return;
    if (fy && fyOf(p.date) !== fy) return;
    const r = row(it.entity || '—');
    r.paidC += cents(p.amount);
    r.count += 1;
    const k = it.kind || 'Other';
    r.byKind[k] = (r.byKind[k] || 0) + cents(p.amount);
  });
  live.forEach((it) => {
    const w = workTaxItem(it, payments, today);
    if (!(w.owing > 0)) return;
    const r = row(it.entity || '—');
    r.owingC += cents(w.owing);
    if (w.status === 'overdue') r.overdueC += cents(w.owing);
  });
  const list = Object.keys(rows).map((k) => {
    const r = rows[k];
    const byKind = {};
    Object.keys(r.byKind).forEach((x) => { byKind[x] = dollars(r.byKind[x]); });
    return { entity: r.entity, paid: dollars(r.paidC), owing: dollars(r.owingC), overdue: dollars(r.overdueC), byKind, count: r.count };
  }).sort((a, b) => b.paid - a.paid || a.entity.localeCompare(b.entity));
  const total = list.reduce((a, r) => a + cents(r.paid), 0);
  const owing = list.reduce((a, r) => a + cents(r.owing), 0);
  return { rows: list, paid: dollars(total), owing: dollars(owing) };
}

// Every FY with a payment or an item in it, newest first.
function taxYears(items, payments) {
  const ys = {};
  livePays(payments).forEach((p) => { const y = fyOf(p.date); if (y) ys[y] = 1; });
  (items || []).filter((i) => !i.deletedAt).forEach((i) => { const y = fyOf(i.dueDate || i.periodEnd); if (y) ys[y] = 1; });
  return Object.keys(ys).map(Number).sort((a, b) => b - a);
}

const sameEntity = (a, b) => {
  if (digits(a.abn).length === 11 && digits(b.abn).length === 11) return digits(a.abn) === digits(b.abn);
  return (a.entity || '').trim().toLowerCase() === (b.entity || '').trim().toLowerCase() && !!(a.entity || '').trim();
};

// A document just read — is it about an item already recorded? (A reminder notice for a
// BAS that's on file, or the same statement uploaded twice.) Same entity AND same period
// end, or same PRN + same amount.
function matchTaxItem(items, x) {
  const live = (items || []).filter((i) => !i.deletedAt);
  if (!x) return null;
  if (isIso(x.periodEnd)) {
    const hit = live.find((i) => sameEntity(i, x) && i.periodEnd === x.periodEnd && (!x.kind || !i.kind || i.kind === x.kind));
    if (hit) return { itemId: hit.id, why: 'same entity and period' };
  }
  const ref = digits(x.prn);
  const amt = cents(x.amount);
  if (ref.length >= 6 && amt > 0) {
    const hit = live.find((i) => digits(i.prn) === ref && cents(i.amount) === amt);
    if (hit) return { itemId: hit.id, why: 'same PRN and amount' };
  }
  return null;
}

// Which unpaid item a bank receipt pays. PRN + the amount owing is near-certain; the PRN
// alone picks the oldest unpaid item on that reference; failing that, a unique amount.
function suggestTaxItem(items, payments, extracted, today) {
  const live = (items || []).filter((i) => !i.deletedAt);
  if (!extracted || !live.length) return null;
  const open = live.map((i) => ({ i: i, w: workTaxItem(i, payments, today) }))
    .filter((o) => o.w.owing > 0)
    .sort((a, b) => (a.i.dueDate || '').localeCompare(b.i.dueDate || ''));
  const ref = digits(extracted.reference);
  const amt = cents(extracted.amount);
  const onRef = ref.length >= 6 ? open.filter((o) => digits(o.i.prn) && (digits(o.i.prn) === ref || ref.indexOf(digits(o.i.prn)) >= 0)) : [];
  const exact = onRef.filter((o) => cents(o.w.owing) === amt);
  if (exact.length) return { itemId: exact[0].i.id, why: 'PRN and amount owing match' };
  if (onRef.length) return { itemId: onRef[0].i.id, why: 'PRN matches (oldest unpaid on it)' };
  const byAmt = open.filter((o) => amt > 0 && cents(o.w.owing) === amt);
  if (byAmt.length === 1) return { itemId: byAmt[0].i.id, why: 'amount matches what is owing' };
  return null;
}

// An ABN is 11 digits with a mod-89 check (subtract 1 from the first digit, weight, sum).
// The read takes it off a "Client ID" line, so check it really is one before storing it.
const ABN_WEIGHTS = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];
function isAbn(s) {
  const d = digits(s);
  if (d.length !== 11) return false;
  let sum = 0;
  for (let k = 0; k < 11; k++) sum += ABN_WEIGHTS[k] * (Number(d[k]) - (k === 0 ? 1 : 0));
  return sum % 89 === 0;
}

// A read ATO document → the fields an item stores.
function itemFromExtract(x) {
  const kind = TAX_KINDS.indexOf(x.kind) >= 0 ? x.kind : (x.kind ? 'Other' : '');
  const docType = DOC_TYPES.indexOf(x.docType) >= 0 ? x.docType : (x.docType ? 'Other' : '');
  return {
    entity: (x.entity || '').toString().trim(),
    abn: isAbn(x.abn) ? digits(x.abn) : '',
    kind: kind,
    docType: docType,
    period: (x.period || '').toString().trim(),
    periodEnd: isIso(x.periodEnd) ? x.periodEnd : '',
    issued: isIso(x.issued) ? x.issued : '',
    amount: x.amount === '' || x.amount == null || Number.isNaN(Number(x.amount)) ? '' : Number(x.amount),
    dueDate: isIso(x.dueDate) ? x.dueDate : '',
    prn: digits(x.prn),
    breakdown: (x.breakdown && typeof x.breakdown === 'object') ? x.breakdown : null,
    notes: (x.summary || '').toString().trim(),
  };
}

// Reminders for unpaid items with a due date — the same stages as plan instalments.
// `sent` keys are "tax|<itemId>|<dueDate>" so they share the ato_plan_alerts record.
function taxReminders(items, payments, sent, today) {
  const next = JSON.parse(JSON.stringify(sent || {}));
  const out = [];
  (items || []).filter((i) => !i.deletedAt && isIso(i.dueDate)).forEach((it) => {
    const w = workTaxItem(it, payments, today);
    const stage = reminderStage({ owing: w.owing, date: it.dueDate, payBy: w.payBy }, today);
    if (!stage) return;
    const key = 'tax|' + it.id + '|' + it.dueDate;
    const done = next[key] || {};
    if (stage === 'overdue' ? done.overdue === today : !!done[stage]) return;
    done[stage] = today;
    next[key] = done;
    out.push({ itemId: it.id, entity: it.entity || '', short: (it.entity || '').replace(/\s+(pty\.?\s*ltd\.?|limited|ltd\.?)$/i, '') + ' ' + (it.kind || 'tax') + (it.period ? ' ' + it.period : ''), prn: it.prn || '', stage: stage, date: it.dueDate, payBy: w.payBy, owing: w.owing });
  });
  return { items: out, sent: next };
}

// Reminders Paul sets himself (the bell on the Tax paid tab): `item.reminders` =
// [{ id, date, note, setAt, cancelledAt }]. Each one pushes ONCE, on the 6am run of its
// date (or the first run after, if a push didn't land). It's a reminder to pay, so it's
// skipped (marked, never sent) once the item is paid. Sent keys "rem|<itemId>|<id>" sit in
// the same ato_plan_alerts record. An item already in today's automatic push just gets the
// note added to that line, so nothing is said twice.
function setReminders(items, payments, sent, today, already) {
  const next = JSON.parse(JSON.stringify(sent || {}));
  const out = [];
  const skipped = [];
  const auto = already || [];
  (items || []).filter((i) => !i.deletedAt).forEach((it) => {
    (it.reminders || []).filter((r) => !r.cancelledAt && isIso(r.date) && r.date <= today).forEach((r) => {
      const key = 'rem|' + it.id + '|' + r.id;
      if (next[key]) return;
      const w = workTaxItem(it, payments, today);
      if (!(w.owing > 0)) { next[key] = { skipped: today }; skipped.push(r.id); return; }
      next[key] = { sent: today };
      const same = auto.filter((a) => a.itemId === it.id)[0];
      if (same) { same.note = [same.note, r.note].filter(Boolean).join(' · ') || same.note; return; }
      out.push({ itemId: it.id, reminderId: r.id, entity: it.entity || '', short: (it.entity || '').replace(/\s+(pty\.?\s*ltd\.?|limited|ltd\.?)$/i, '') + ' ' + (it.kind || 'tax') + (it.period ? ' ' + it.period : ''), prn: it.prn || '', stage: 'reminder', note: (r.note || '').toString().trim(), date: it.dueDate || '', payBy: w.payBy, owing: w.owing });
    });
  });
  return { items: out, sent: next, skipped: skipped };
}

// The reminder to show for an item: the next one still to come, else the last one that
// went (so the bell can say "reminded 9 Nov"), or null. `today` defaults to now.
function nextReminder(item, today) {
  const t = today || todayIso();
  const live = ((item && item.reminders) || []).filter((r) => !r.cancelledAt && isIso(r.date));
  live.sort((a, b) => a.date.localeCompare(b.date));
  const ahead = live.filter((r) => r.date >= t);
  return ahead[0] || live[live.length - 1] || null;
}

module.exports = { TAX_KINDS, DOC_TYPES, fyOf, fyLabel, workTaxItem, taxSummary, taxYears, matchTaxItem, suggestTaxItem, itemFromExtract, isAbn, taxReminders, setReminders, nextReminder };
